import { Button, Grid, Stack, Typography } from "@mui/material";
import type { BatchPaymentDetailDto } from "Infrastructure/Api/Api";
import { BatchPaymentStatusChip } from "Components/BatchPayments/BatchPaymentStatusChip";
import { GridItem } from "Components/Shared/GridItem";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Resources, useResource } from "Translations/Resources";
import { getLocalBankAccountNumberFromIBAN } from "Utils/AccountUtils";
import { formatCurrency } from "Utils/CurrencyUtils";
import { formatDate } from "Utils/DateUtils";

const RESOURCES = Resources.Banking.BatchPayments;

type Props = {
  data: BatchPaymentDetailDto;
};

export const BatchPaymentHeader = ({ data }: Props) => {
  const { t } = useResource();
  const {
    status,
    bankAccountIBAN,
    totalAmount,
    currencyCode,
    paymentCount,
    dateCreated,
    authorizedAt,
    completedAt,
    signingUrl,
  } = data;

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{t(RESOURCES.Title)}</Typography>
        <BatchPaymentStatusChip status={status} />
      </Stack>

      <Grid container spacing={2}>
        <GridItem
          label={t(RESOURCES.Account)}
          value={getLocalBankAccountNumberFromIBAN(bankAccountIBAN)}
          columns={2}
        />
        <GridItem
          label={t(RESOURCES.TotalAmount)}
          value={formatCurrency({ amount: totalAmount, currencyCode })}
          columns={2}
        />
        <GridItem
          label={t(RESOURCES.PaymentCount)}
          value={String(paymentCount)}
          columns={2}
        />
        <GridItem
          label={t(RESOURCES.CreatedAt)}
          value={formatDate(dateCreated, true)}
          columns={2}
        />
        {authorizedAt && (
          <GridItem
            label={t(RESOURCES.AuthorizedAt)}
            value={formatDate(authorizedAt, true)}
            columns={2}
          />
        )}
        {completedAt && (
          <GridItem
            label={t(RESOURCES.CompletedAt)}
            value={formatDate(completedAt, true)}
            columns={2}
          />
        )}
      </Grid>

      {status === "Initiated" && signingUrl && (
        <Button
          variant="contained"
          startIcon={<OpenInNewIcon />}
          onClick={() => window.open(signingUrl, "_blank")}
        >
          {t(RESOURCES.SignInBank)}
        </Button>
      )}
    </Stack>
  );
};
